import type { ConfigurationPackage } from '@festina/configuration-general-types/configuration'
import { urlUtils } from '@festina-life/core'
import { useCallback, useMemo } from 'react'

import { useParameterDefinitionId } from '../../../hooks/useParameterDefinitionId'
import { useNavigationParams } from '../../../system/useNavigationParams'
import { useMutateConvertConfigurationPackageToParameterValuesCsv } from '../../../uiModels/api/parameters/useMutateConvertConfigurationPackageToParameterValuesCsv'
import { useLazyLoadAllParameterValuesForView } from '../../../uiModels/parameters/useLazyLoadAllParameterValuesForView'

export const useDownloadValues = () => {
  const { mutateAsync: loadAllValues, isPending: isLoadingAllValues } =
    useLazyLoadAllParameterValuesForView(true)
  const parameterDefinitionId = useParameterDefinitionId()
  const { mutateAsync: convertPackage, isPending: isConvertingPackage } =
    useMutateConvertConfigurationPackageToParameterValuesCsv()
  const { layer } = useNavigationParams()

  const mutateAsync = useCallback(async () => {
    const allValues = await loadAllValues({ parameterDefinitionId })
    const createdPackage = {
      _type: 'ConfigurationPackage',
      changesets: [
        {
          _type: 'ConfigurationChangeset',
          tenantId: layer.tenantId,
          layer: layer.layerId,
          operations: allValues.map(
            (valueWithSourceInfo) => valueWithSourceInfo.internalOperation,
          ),
        },
      ],
    } satisfies ConfigurationPackage
    const csvBlob = await convertPackage(createdPackage)
    urlUtils.downloadAsFile(
      csvBlob,
      `${parameterDefinitionId}-flattened-values.csv`,
    )
  }, [
    parameterDefinitionId,
    layer.tenantId,
    loadAllValues,
    convertPackage,
    layer.layerId,
  ])

  return useMemo(
    () => ({
      mutateAsync,
      isPending: isLoadingAllValues || isConvertingPackage,
    }),
    [isConvertingPackage, isLoadingAllValues, mutateAsync],
  )
}
